import { computed } from 'vue'
import { useRouter, useRoute, onBeforeRouteUpdate } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'

import { useCompanyStore } from '@/stores/companyStore'

import { parseFromBase64 } from '@/helpers/stringHelper.js'

import {
	ROUTER_QUERY_KEYS,
	ROUTER_VIEW_VARIANTS_OF_ENTITY_LISTS_CONFIG,
	NAVIGATION_ROUTE_TYPES,
} from '@/config/router/routerMapConfig.js'
import { setPageHeader } from '@/helpers/pageHelper'

export default function useRouts() {
	const router = useRouter()
	const route = useRoute()

	const companyStore = useCompanyStore()
	const { currentCompany } = storeToRefs(companyStore)

	const { t: $t } = useI18n()

	const pathToBack = computed(() => {
		const backQueryParams =  route.query[ROUTER_QUERY_KEYS.BACK_PATH_PARAMS]
		if (backQueryParams) {
			const params = parseFromBase64(backQueryParams)
			return params
		}
		return null
	})

	const currentGroupName = computed(() => {
		return route.matched.find((el) => (el.meta.navigationRouteType === NAVIGATION_ROUTE_TYPES.group.key))?.name ?? null
	})

	const getPageTitle = (_params, _options) => {
		const params = Object.assign({}, _params)
		const options = Object.assign({
			addOnTrack: true,
			addCompanyName: true,
		}, _options)

		let title = params.title
		if (params.entityName) title += `: ${params.entityName}`
		if (params.view) title += ` (${params.view})`
		if (params.hint) title += ` / ${params.hint}`

		if (options.addCompanyName) {
			const companyName = currentCompany.value.name
			if (companyName) title += ` - ${companyName}`
		}

		if (options.addOnTrack) title += ` | OnTrack`

		return title
	}

	const setRoutePageTitle = (title) => {
		setPageHeader(title)
	}

	const setRoutePageAutoTitle = () => {
		return new Promise((resolve) => {
			if (NAVIGATION_ROUTE_TYPES.view.key === route.meta.navigationRouteType) {

				// мы находимся в одном из стандартных наших видов отображения массива данных
				const viewRoute = route.matched.find(({ meta }) => (meta.navigationRouteType === NAVIGATION_ROUTE_TYPES.view.key))
				const moduleRoute = route.matched.find(({ meta }) => (meta.navigationRouteType === NAVIGATION_ROUTE_TYPES.module.key))
				const groupRoute = route.matched.find(({ meta }) => (meta.navigationRouteType === NAVIGATION_ROUTE_TYPES.group.key))

				let title = null
				let view = null
				let hint = null

				if (viewRoute) {
					const viewVariant = viewRoute.path.split('/').at(-1)
					const viewKey = ROUTER_VIEW_VARIANTS_OF_ENTITY_LISTS_CONFIG[viewVariant].translationKey
					if (viewKey) view = $t(viewKey)
				}

				if (moduleRoute) {
					const titleKey = moduleRoute.meta?.titleTranslationKey
					if (titleKey) title = $t(titleKey)
				}

				if (groupRoute) {
					const hintKey = groupRoute.meta?.titleTranslationKey
					if (hintKey) hint = $t(hintKey)
				}

				const pageTitle = getPageTitle({
					title,
					view,
					hint,
				})
				setRoutePageTitle(pageTitle)

			} else if (NAVIGATION_ROUTE_TYPES.referenceMaterials.key === route.meta.navigationRouteType) {

				const titleKey = route.meta?.titleTranslationKey
				let title = null
				if (titleKey) title = $t(titleKey)
				const pageTitle = getPageTitle({
					title,
				})
				setRoutePageTitle(pageTitle)

			} else if (NAVIGATION_ROUTE_TYPES.module.key === route.meta.navigationRouteType) {

				const moduleRoute = route.matched.find(({ meta }) => (meta.navigationRouteType === NAVIGATION_ROUTE_TYPES.module.key))
				const groupRoute = route.matched.find(({ meta }) => (meta.navigationRouteType === NAVIGATION_ROUTE_TYPES.group.key))

				let title = null
				let hint = null

				if (moduleRoute) {
					const titleKey = moduleRoute.meta?.titleTranslationKey
					if (titleKey) title = $t(titleKey)
				}

				if (groupRoute) {
					const hintKey = groupRoute.meta?.titleTranslationKey
					if (hintKey) hint = $t(hintKey)
				}

				const pageTitle = getPageTitle({
					title,
					hint,
				})
				setRoutePageTitle(pageTitle)

			}
			resolve()
		})
	}

	return {
		router,
		route,
		onBeforeRouteUpdate,

		pathToBack,

		currentGroupName,

		getPageTitle,

		setRoutePageTitle,
		setRoutePageAutoTitle,
	}
}
